// FormPreviewPanel.js
import React, { useMemo } from "react";
import "./FormPreviewPanel.css";

/**
 * Resolve preview options via ViewFields; fallback to inline array on the control.
 * Returns [{ name, value }] for rendering.
 */
function resolveOptionsForPreview(control, viewfieldsInstance) {
  try {
    const out = viewfieldsInstance?.resolveOptionsForFieldExternal?.(control);
    const flat = Array.isArray(out?.flatOptions) ? out.flatOptions : [];
    return flat.map((o) => ({
      name: o?.name ?? "(Missing option name)",
      value: o?.value,
    }));
  } catch {
    const arr = Array.isArray(control?.options) ? control.options : [];
    return arr.map((o) => ({
      name: o?.name ?? "(Missing option name)",
      value: o?.value,
    }));
  }
}

export default function FormPreviewPanel({
  selection,
  lang,
  pages,
  dictionaryResolvers,
  viewfieldsInstance,
}) {
  const resolved = useMemo(() => {
    if (!Array.isArray(pages)) return { page: null, controls: [] };

    if (selection?.kind === "page") {
      const page = pages[selection.pageIndex] || null;
      return { page, controls: page?.controls || [] };
    }

    if (selection?.kind === "control") {
      const page = pages[selection.pageIndex] || null;
      const ctrl = page?.controls?.[selection.controlIndex] || null;
      return { page, controls: ctrl ? [ctrl] : [] };
    }

    return { page: null, controls: [] };
  }, [pages, selection]);

  if (selection?.kind === "form") {
    return <div className="fe-preview__empty">Select a page or control to preview.</div>;
  }

  if (!resolved.page) {
    return <div className="fe-preview__empty">Nothing to preview.</div>;
  }

  return (
    <div className="pv-root">
      {resolved.controls.length === 0 ? (
        <div className="fe-preview__empty">This page has no controls.</div>
      ) : (
        resolved.controls.map((ctrl, idx) => (
          <ControlPreview
            key={ctrl?.key || `ctrl-${idx}`}
            control={ctrl}
            lang={lang}
            dictionaryResolvers={dictionaryResolvers}
            viewfieldsInstance={viewfieldsInstance}
            selected={selection?.kind === "control"}
          />
        ))
      )}
    </div>
  );
}

/* ------------------------ Control renderer ------------------------ */

function ControlPreview({ control, lang, dictionaryResolvers, viewfieldsInstance, selected }) {
  if (!control) return null;

  const getPhraseSync = dictionaryResolvers?.getPhraseSync;
  const getPhraseRawSync = dictionaryResolvers?.getPhraseRawSync;
  const hasPhraseSync = dictionaryResolvers?.hasPhraseSync;

  // Helpers to avoid coupling preview directly to resolver fallback policy
  const phraseHasKey = (l, key) => {
    if (!key) return false;

    // Prefer explicit introspection API if available
    if (typeof hasPhraseSync === "function") {
      return hasPhraseSync(l, key);
    }
    if (typeof getPhraseRawSync === "function") {
      return typeof getPhraseRawSync(l, key) === "string";
    }

    // Fallback: infer from getPhraseSync; treat "returns key unchanged" as missing
    if (typeof getPhraseSync === "function") {
      const v = getPhraseSync(l, key);
      return typeof v === "string" && v !== key;
    }

    return false;
  };

  const phraseGet = (l, key) => {
    if (!key) return undefined;

    if (typeof getPhraseRawSync === "function") {
      return getPhraseRawSync(l, key);
    }
    if (typeof getPhraseSync === "function") {
      const v = getPhraseSync(l, key);
      return typeof v === "string" && v !== key ? v : undefined;
    }

    return undefined;
  };

  // Candidate keys: label/content first, then key. No synthetic "_label".
  const candidates = [
    control?.label || null,
    control?.content || null,
    control?.key || null,
  ].filter(Boolean);

  let questionKey = "";
  for (const k of candidates) {
    if (phraseHasKey(lang, k)) {
      questionKey = k;
      break;
    }
  }
  if (!questionKey && candidates.length > 0) {
    // Fall back to last candidate (usually control.key) purely as a key,
    // even if there is currently no dictionary phrase for it.
    questionKey = candidates[candidates.length - 1];
  }

  const phraseHtml = questionKey ? (phraseGet(lang, questionKey) ?? "") : "";
  const hasPhrase = !!questionKey && phraseHasKey(lang, questionKey);

  // Resolve options (asset or inline via viewFieldsInstance)
  const options = resolveOptionsForPreview(control, viewfieldsInstance);

  const type = String(control.type || "").toUpperCase();
  const isRequired = !!control.required;

  return (
    <div className={`pv-control ${selected ? "is-selected" : ""}`}>
      {/* If phrase exists, render HTML; else show key as missing placeholder */}
      {hasPhrase ? (
        <div className="pv-label" dangerouslySetInnerHTML={{ __html: phraseHtml }} />
      ) : (
        <div className="pv-label pv-label--missing">
          {questionKey ? `(${questionKey})` : "(No label/content key)"}
        </div>
      )}

      <div className="pv-field">
        {renderField(type, options, control?.attributes, isRequired)}
      </div>
    </div>
  );
}

function renderField(type, options, attributes, required) {
  // Preview controls should behave like real controls, but their events
  // shouldn't bubble out and trigger selection / other editor actions.
  const handlePreviewChange = (e) => {
    e.stopPropagation(); // <- no preventDefault here
  };

  switch (type) {
    case "SELECT":
      return (
        <select
          className="pv-input"
          aria-required={required}
          onChange={handlePreviewChange}
        >
          <option value="">— Select —</option>
          {options.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.name}
            </option>
          ))}
        </select>
      );

    case "RADIO":
      return (
        <div
          className="pv-group"
          aria-required={required}
          onChange={handlePreviewChange}
        >
          {options.map((opt, i) => (
            <label key={String(opt.value)} className="pv-option">
              <input type="radio" name={`pv-radio-preview-${i}`} />
              <span>{opt.name}</span>
            </label>
          ))}
        </div>
      );

    case "CHECKBOX":
      return (
        <div
          className="pv-group"
          aria-required={required}
          onChange={handlePreviewChange}
        >
          {options.map((opt, i) => (
            <label key={String(opt.value)} className="pv-option">
              <input type="checkbox" name={`pv-checkbox-preview-${i}`} />
              <span>{opt.name}</span>
            </label>
          ))}
        </div>
      );

    case "TEXT": {
      const h = Number(attributes?.height || 0);
      if (h > 1) {
        return (
          <textarea
            className="pv-input"
            rows={Math.min(h, 8)}
            placeholder=""
            onChange={handlePreviewChange}
          />
        );
      }
      return (
        <input
          className="pv-input"
          type="text"
          placeholder=""
          onChange={handlePreviewChange}
        />
      );
    }

    case "SLIDER": {
      const min = Number(attributes?.min ?? 0);
      const max = Number(attributes?.max ?? 100);
      const step = Number(attributes?.step ?? 1);
      return (
        <div className="pv-slider" onChange={handlePreviewChange}>
          <input type="range" min={min} max={max} step={step} />
          <div className="pv-slider__ticks">
            <span>{min}</span>
            <span>{max}</span>
          </div>
        </div>
      );
    }

    case "RATING": {
      const max = Math.max(3, Number(attributes?.max ?? 5));
      return (
        <div
          className="pv-rating"
          role="img"
          aria-label={`Rating out of ${max}`}
        >
          {Array.from({ length: max }).map((_, i) => (
            <span
              key={i}
              className="pv-star"
              aria-hidden="true"
              onClick={handlePreviewChange}
            >
              ★
            </span>
          ))}
        </div>
      );
    }

    case "INFORMATION": {
      const html = String(attributes?.html || "");
      if (html) {
        return (
          <div
            className="pv-info"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      }
      return <div className="pv-info" />;
    }

    case "SUMMARY":
    case "SUBMISSIONLIST":
      return <div className="pv-placeholder" />;

    default:
      return (
        <div className="pv-placeholder">
          (Unsupported control type: {type || "UNKNOWN"})
        </div>
      );
  }
}

